// The checkout tunnel (sculpt-api PLAN-payments.md M3): one page, three steps — the offer (or a credit pack), the
// billing profile, the payment — beside a summary that always shows what is paid today and what follows each month.
// The price moves as the admin chooses (computed here at once, then the API's quote, which knows prorata, the
// period's end and the VAT); the profile is filled in the tunnel when it is missing; one button places the order and
// leaves for Mollie's page. `?offer=&tier=&seats=` preselect (the website's pricing page links here), `?pack=` opens
// the credits, `?order=` pays an order already placed. Mollie sends the admin back to `/billing?order={id}`. Prices
// are in the organization's currency once it is fixed, else in `?currency=` or the API's guess, which the admin may
// switch (components/currency.tsx); the order is placed in the currency shown. Saving the billing profile fixes it.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { FREE_OFFER, PriceBreakdown, lowUsageWarning, monthlyTotal, offerFeatures, offerPrice, sharedCredits, tiersOf, vatRate } from '../components/billing';
import { BillingProfileForm } from '../components/billingProfile';
import { inOtherMemberState } from '../components/countries';
import { ManageCookies } from '../components/CookieBanner';
import { CurrencySwitcher, shownCurrency } from '../components/currency';
import { AcceptTerms } from '../components/legal';
import { date, money } from '../components/format';
import { Alert, Badge, Button, Spinner } from '../components/ui';
import { ApiError, api } from '../services/api';
import { useSession } from '../session';
import type { BillingState, CreditPack, Offer, Order, Organization, PackQuote, Quote, VatFields } from '../types';

export const PREVIEW_DELAY_MS = 300;
const PROFILE_FORM = 'checkout-profile';
/** The width from which the summary sits beside the steps (Tailwind's `lg`). */
const DESKTOP_QUERY = '(min-width: 1024px)';
const CHANGE_ERRORS = ['seats_below_members', 'seats_out_of_range', 'no_change', 'trial_not_selectable', 'free_not_selectable', 'tier_required', 'tier_not_offered', 'packs_require_paid_offer', 'offer_not_found', 'pack_not_found',
  'currency_mismatch', 'currency_locked', 'currency_not_offered'];
const PAY_ERRORS = ['payments_disabled', 'order_not_payable', 'vat_review_pending', 'billing_profile_required', 'nothing_to_pay', 'payment_provider_unavailable'];

type Step = 1 | 2 | 3;
type Mode = 'offer' | 'pack' | 'order';
type Action = { label: string; onClick?: () => void; form?: string; disabled: boolean; testId: string };
type Done = { kind: 'applied' } | { kind: 'scheduled'; date: string | null } | { kind: 'ordered'; order: Order };

function refusal(code: string, t: (k: string) => string): string {
  if (CHANGE_ERRORS.includes(code)) return t(`billing.error.${code}`);
  if (PAY_ERRORS.includes(code)) return t(`billing.card.error.${code}`);
  return t('app.error');
}

export function CheckoutPage() {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const { user, reload: reloadUser } = useSession();
  const [params] = useSearchParams();
  const admin = user?.organization?.role === 'owner' || user?.organization?.role === 'admin';
  const orderParam = Number(params.get('order')) || null;

  const [billing, setBilling] = useState<BillingState | null>(null);
  const [org, setOrg] = useState<Organization | null>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [existing, setExisting] = useState<Order | null>(null);
  const [currency, setCurrency] = useState('');
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [loadError, setLoadError] = useState(false);

  const [mode, setMode] = useState<Mode>(orderParam ? 'order' : params.get('pack') ? 'pack' : 'offer');
  const [key, setKey] = useState('');
  const [tier, setTier] = useState('');
  const [seats, setSeats] = useState(1);
  const [packKey, setPackKey] = useState(params.get('pack') ?? '');
  const [step, setStep] = useState<Step>(orderParam ? 3 : 1);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileBlocked, setProfileBlocked] = useState(false);
  const desktop = useDesktop();
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Done | null>(null);

  // ── loading, and the selection the URL asks for ──
  const load = useCallback(async () => {
    try {
      const [b, o] = await Promise.all([api.subscription(), api.organization()]);
      const shown = await shownCurrency(o, params.get('currency'));
      const [all, p] = await Promise.all([api.offers(shown.currency), api.creditPacks(shown.currency)]);
      setBilling(b);
      setOrg(o);
      setCurrency(shown.currency);
      setCurrencies(shown.choices);
      setPacks(p);
      const sellable = all.filter((x) => !x.trial && x.key !== FREE_OFFER);
      setOffers(sellable);
      if (orderParam) {
        setExisting(await api.order(orderParam));
        if (!o.billing_complete) setStep(2);
      }
      const sub = b.subscription;
      const onPaid = sub !== null && !sub.trial && Number(sub.monthly_total) > 0;
      const wanted = sellable.find((x) => x.key === params.get('offer')) ?? (onPaid ? sellable.find((x) => x.key === sub.offer_key) : undefined) ?? sellable[0];
      if (wanted) {
        setKey(wanted.key);
        const tiers = tiersOf(wanted);
        const has = (price: string | null | undefined) => tiers.find((x) => price != null && Number(x.price) === Number(price))?.price;
        setTier(has(params.get('tier')) ?? has(sub?.offer_key === wanted.key ? sub.credit_tier_price : null) ?? tiers[0]?.price ?? '');
        setSeats(seatsWithin(wanted, Number(params.get('seats')) || (sub?.offer_key === wanted.key ? sub.seats : sub?.seats_used ?? 1), sub?.seats_used ?? 1));
      }
      if (!params.get('pack') && p.length && !packKey) setPackKey(p[0].key);
    } catch {
      setLoadError(true);
    }
    // The URL is read once, when the tunnel opens.
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const sub = billing?.subscription ?? null;
  const offer = offers.find((o) => o.key === key) ?? null;
  const pack = packs.find((p) => p.key === packKey) ?? null;
  const used = sub?.seats_used ?? 1;
  const onPaidOffer = sub !== null && !sub.trial && Number(sub.monthly_total) > 0;

  // The catalogue in another currency: the same offers and packs under the same keys, so the choice stays; a tier is
  // matched by its rank, as the API matches them.
  const switchCurrency = async (next: string) => {
    try {
      const [all, p] = await Promise.all([api.offers(next), api.creditPacks(next)]);
      const sellable = all.filter((x) => !x.trial && x.key !== FREE_OFFER);
      const rank = offer ? tiersOf(offer).findIndex((x) => Number(x.price) === Number(tier)) : -1;
      const same = sellable.find((x) => x.key === key) ?? sellable[0];
      setOffers(sellable);
      setPacks(p);
      setCurrency(next);
      if (same) {
        setKey(same.key);
        const tiers = tiersOf(same);
        setTier(tiers[rank]?.price ?? tiers[0]?.price ?? '');
        setSeats(seatsWithin(same, seats, used));
      }
      if (p.length && !p.some((x) => x.key === packKey)) setPackKey(p[0].key);
    } catch {
      setLoadError(true);
    }
  };

  // ── the quote: at once from the catalogue, then the API's (prorata, period end, VAT) ──
  const [quote, setQuote] = useState<{ for: string; quote: Quote | null; error: string | null } | null>(null);
  const [profileVersion, setProfileVersion] = useState(0);
  const selection = `${key}|${seats}|${tier}|${currency}|${profileVersion}`;
  useEffect(() => {
    if (mode !== 'offer' || !offer || !admin) return;
    let live = true;
    const timer = setTimeout(() => {
      api
        .previewChange(offer.key, seats, tiersOf(offer).length ? tier : null, currency)
        .then((q) => live && setQuote({ for: selection, quote: q, error: null }))
        .catch((err) => live && setQuote({ for: selection, quote: null, error: err instanceof ApiError ? err.code : 'error' }));
    }, PREVIEW_DELAY_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [mode, offer, admin, seats, tier, currency, selection]);
  // A pack's price and VAT, before it is ordered.
  const [packQuote, setPackQuote] = useState<{ for: string; quote: PackQuote | null; error: string | null } | null>(null);
  const packSelection = `${packKey}|${profileVersion}`;
  useEffect(() => {
    if (mode !== 'pack' || !packKey || !admin || !onPaidOffer) return;
    let live = true;
    api
      .previewPack(packKey)
      .then((pq) => live && setPackQuote({ for: packSelection, quote: pq, error: null }))
      .catch((err) => live && setPackQuote({ for: packSelection, quote: null, error: err instanceof ApiError ? err.code : 'error' }));
    return () => {
      live = false;
    };
  }, [mode, packKey, admin, onPaidOffer, packSelection]);
  const pq = packQuote && packQuote.for === packSelection ? packQuote.quote : null;
  const packError = packQuote && packQuote.for === packSelection ? packQuote.error : null;
  const current = quote && quote.for === selection ? quote : null;
  const q = current?.quote ?? null;
  const quoteError = current?.error ?? null;
  const estimate = offer ? monthlyTotal(offer, tiersOf(offer).length ? tier : null, seats) : 0;
  // The current terms chosen again: the API says `no_change` (`free_not_selectable` for Free); before its answer,
  // the subscription already tells.
  const sameAsSub = !!sub && !!offer && sub.offer_key === offer.key && sub.seats === seats
    && Number(sub.credit_tier_price ?? 0) === Number(tiersOf(offer).length ? tier : 0);
  const unchanged = mode === 'offer' && (quoteError === 'no_change' || (sameAsSub && (!current || quoteError === 'free_not_selectable')));

  // What is paid today and whether the payment and the profile are needed at all.
  const needsPayment = mode === 'order' ? true : mode === 'pack' ? true : !!q && q.applies === 'on_payment' && Number(q.amount) > 0;
  const nothingToPay = mode === 'offer' && !!q && (q.applies === 'period_end' || Number(q.amount) === 0);
  const canLeaveStep1 = mode === 'pack' ? !!pack && onPaidOffer : mode === 'offer' ? !!q && !quoteError : true;

  // The billing step only when the profile is missing (or « Modifier » in the payment step asks for it).
  const next = () => {
    setError(null);
    setStep(needsPayment && !org?.billing_complete ? 2 : 3);
  };

  const profileSaved = async (saved: Organization) => {
    setOrg(saved);
    // The billing country fixes the currency (the API moved the organization to the country's): prices in it.
    if (saved.currency_fixed) {
      setCurrencies([]);
      if (saved.currency !== currency) await switchCurrency(saved.currency);
    }
    setProfileVersion((v) => v + 1);
    if (existing) setExisting(await api.order(existing.id));        // the API re-stamps a pending order's VAT, and re-prices it in that currency
    if (saved.billing_complete) setStep(3);
  };

  // ── paying ──
  const leaveFor = async (order: Order) => {
    const out = await api.checkout(order.id);
    if (!out.checkout_url) throw new ApiError(502, 'payment_provider_unavailable', '');
    window.location.assign(out.checkout_url);
  };

  const pay = async () => {
    if (!billing) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'order' && existing) {
        if (billing.payments_enabled) return await leaveFor(existing);
        setDone({ kind: 'ordered', order: existing });
      } else if (mode === 'pack' && pack) {
        const order = await api.buyCredits(pack.key);
        if (billing.payments_enabled && order.vat_scheme !== 'review') return await leaveFor(order);
        setDone({ kind: 'ordered', order });
      } else if (mode === 'offer' && q) {
        const result = await api.changeSubscription(q.offer_key, q.seats, q.credit_tier_price, q.currency);
        if (result.applied) setDone({ kind: 'applied' });
        else if (!result.order) setDone({ kind: 'scheduled', date: result.quote.period_end });
        else if (billing.payments_enabled && result.order.vat_scheme !== 'review' && Number(result.order.total ?? result.order.amount) > 0) return await leaveFor(result.order);
        else setDone({ kind: 'ordered', order: result.order });
        await reloadUser();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'error');
    }
    setBusy(false);
  };

  // ── rendering ──
  if (loadError) return <Frame><Alert kind="error">{t('app.error')}</Alert></Frame>;
  if (!billing || !org || !user) return <Frame><Spinner /></Frame>;
  if (!admin)
    return (
      <Frame>
        <Alert kind="info">{t('checkout.adminsOnly')}</Alert>
      </Frame>
    );
  if (done) return <Frame><DoneScreen done={done} billing={billing} /></Frame>;

  const vat: VatFields | null = mode === 'order' ? existing : mode === 'offer' ? q : pq;
  // What the amounts below are in: the order's or the quote's, else the catalogue's shown.
  const amountCurrency = (mode === 'order' ? existing?.currency : mode === 'offer' ? q?.currency : pq?.currency ?? pack?.currency) ?? currency;
  const payLabel = (() => {
    if (nothingToPay) return t('checkout.confirmChange');
    if (!billing.payments_enabled) return t('checkout.placeOrder');
    const total = vat?.total ?? null;
    return total !== null ? t('checkout.pay', { total: money(total, amountCurrency, l) }) : t('checkout.payPack');
  })();
  const blocked = (mode === 'order' && (!existing || existing.status !== 'pending' || existing.vat_scheme === 'review')) || (needsPayment && !org.billing_complete);
  // The step's one primary action: under the step on a desktop, in the summary bar on a phone (always in reach).
  const primary: Action =
    step === 1
      ? { label: t('checkout.continue'), onClick: next, disabled: !canLeaveStep1, testId: 'next-1' }
      : step === 2
        ? { label: savingProfile ? t('app.saving') : t('checkout.continue'), form: PROFILE_FORM, disabled: savingProfile || profileBlocked, testId: 'next-2' }
        : {
            label: busy ? t('billing.card.redirecting') : payLabel,
            onClick: () => void pay(),
            disabled: busy || blocked || !accepted || (!billing.payments_enabled && !billing.payment_instructions && !nothingToPay),
            testId: 'pay',
          };
  const back = step === 2 ? () => setStep(existing ? 3 : 1) : step === 3 && mode !== 'order' ? () => setStep(1) : null;
  const actions = (
    <div className="flex items-center justify-between gap-3">
      {back ? (
        <Button variant="ghost" onClick={back}>
          {t('app.back')}
        </Button>
      ) : (
        <span />
      )}
      {desktop && <PrimaryButton action={primary} />}
    </div>
  );

  return (
    <Frame>
      <Stepper step={step} skipBilling={nothingToPay} onGo={(s) => s < step && (mode !== 'order' || s > 1) && setStep(s)} />
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        <div className="flex flex-col gap-4 pb-40 lg:pb-0">
          {step === 1 && (
            <section className="flex flex-col gap-4" data-testid="step-offer">
              {(mode === 'offer' || mode === 'pack') && (
                <div className="flex gap-1 rounded-md bg-hover p-1 text-[13px]" role="tablist">
                  {(['offer', 'pack'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      role="tab"
                      aria-selected={mode === m}
                      onClick={() => setMode(m)}
                      className={`flex-1 rounded-sm px-3 py-1.5 ${mode === m ? 'bg-white font-medium text-ink shadow-xs' : 'text-muted hover:text-ink'}`}
                    >
                      {m === 'offer' ? t('checkout.tabOffer') : t('checkout.tabCredits')}
                    </button>
                  ))}
                </div>
              )}
              {(mode === 'offer' || mode === 'pack') && <CurrencySwitcher value={currency} choices={currencies} onChange={(c) => void switchCurrency(c)} />}
              {mode === 'offer' && (
                <OfferChoice
                  offers={offers}
                  current={sub?.offer_key ?? null}
                  selected={key}
                  onSelect={(o) => {
                    setKey(o.key);
                    const tiers = tiersOf(o);
                    setTier(tiers.find((x) => Number(x.price) === Number(tier))?.price ?? tiers[0]?.price ?? '');
                    setSeats(seatsWithin(o, seats, used));
                  }}
                />
              )}
              {mode === 'offer' && offer && tiersOf(offer).length > 0 && <TierChoice offer={offer} value={tier} onChange={setTier} />}
              {mode === 'offer' && offer && offer.terms.max_seats !== 1 && <SeatsChoice offer={offer} seats={seats} used={used} onChange={setSeats} />}
              {mode === 'offer' && offer && (() => {
                const warning = lowUsageWarning(offer, tier, seats, t, l);
                return warning ? <Alert kind="warn">{warning}</Alert> : null;
              })()}
              {mode === 'offer' && quoteError && <Alert kind={unchanged ? 'info' : 'error'}>{unchanged ? t('checkout.noChange') : refusal(quoteError, t)}</Alert>}
              {mode === 'pack' && packError && <Alert kind="error">{refusal(packError, t)}</Alert>}
              {mode === 'pack' && (onPaidOffer ? <PackChoice packs={packs} selected={packKey} onSelect={setPackKey} /> : <Alert kind="info">{t('billing.error.packs_require_paid_offer')}</Alert>)}
              {actions}
            </section>
          )}

          {step === 2 && (
            <section className="flex flex-col gap-4 rounded-lg border border-line-card bg-white p-5 sm:p-6" data-testid="step-billing">
              <p className="text-[13px] text-muted">{t('checkout.profileWhy')}</p>
              <BillingProfileForm org={org} admin formId={PROFILE_FORM} onBusy={setSavingProfile} onBlocked={setProfileBlocked} onSaved={(o) => void profileSaved(o)} />
              {org.vat_status === 'review' && inOtherMemberState(org.billing_country) && <Alert kind="warn">{t('checkout.vatReview')}</Alert>}
              {actions}
            </section>
          )}

          {step === 3 && (
            <section className="flex flex-col gap-4 rounded-lg border border-line-card bg-white p-5 sm:p-6" data-testid="step-payment">
              <h2 className="text-[16px] font-medium tracking-[-0.01em] text-ink">{nothingToPay ? t('checkout.confirmTitle') : t('checkout.paymentTitle')}</h2>
              {mode === 'order' && existing && existing.status !== 'pending' && <Alert kind="info">{t('billing.card.error.order_not_payable')}</Alert>}
              {mode === 'order' && existing?.vat_scheme === 'review' && <Alert kind="warn">{t('billing.vat.held')}</Alert>}
              {needsPayment && org.billing_complete && <ProfileSummary org={org} onEdit={() => setStep(2)} />}
              {needsPayment && !org.billing_complete && (
                <Alert kind="warn">
                  {t('checkout.profileMissing')}{' '}
                  <button type="button" className="underline" onClick={() => setStep(2)}>
                    {t('billing.vat.noProfileLink')}
                  </button>
                </Alert>
              )}
              {nothingToPay ? (
                <p className="text-[13px] text-muted">
                  {q?.applies === 'period_end' ? t('checkout.scheduledHint', { date: date(q.period_end, l) }) : t('checkout.freeHint')}
                </p>
              ) : (
                <Amounts vat={vat} amount={mode === 'pack' ? pq?.amount ?? pack?.price ?? '0' : mode === 'order' ? existing?.amount ?? '0' : q?.amount ?? '0'} currency={amountCurrency} />
              )}
              {/* Every order, Free and scheduled changes included: placing it accepts the Terms of sale (DEC-05). */}
              <label className="flex items-start gap-2 text-[13px] text-ink">
                <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 rounded-sm text-accent focus:ring-0" data-testid="accept-terms" />
                <span>
                  <AcceptTerms />
                </span>
              </label>
              {!nothingToPay && billing.payments_enabled && <p className="text-[12.5px] text-muted">{t('checkout.secure')}</p>}
              {!nothingToPay && !billing.payments_enabled && <Alert kind="info">{billing.payment_instructions ? t('checkout.transferHint') : t('billing.card.error.payments_disabled')}</Alert>}
              {error && (
                <Alert kind="error">
                  {refusal(error, t)}
                  {error === 'billing_profile_required' && (
                    <>
                      {' '}
                      <button type="button" className="underline" onClick={() => setStep(2)}>
                        {t('billing.vat.noProfileLink')}
                      </button>
                    </>
                  )}
                </Alert>
              )}
              {actions}
            </section>
          )}
        </div>
        <Summary mode={mode} unchanged={unchanged} offer={offer} tier={tier} seats={seats} pack={pack} order={existing} quote={q} estimate={estimate} sub={sub} packQuote={pq} loading={(mode === 'offer' && !current) || (mode === 'pack' && onPaidOffer && !packQuote)} profileComplete={org.billing_complete} action={desktop ? null : primary} />
      </div>
    </Frame>
  );
}

/** Whether the summary sits beside the steps; without `matchMedia` (tests), a desktop. */
function useDesktop(): boolean {
  const [media] = useState(() => (typeof window.matchMedia === 'function' ? window.matchMedia(DESKTOP_QUERY) : null));
  const [desktop, setDesktop] = useState(media ? media.matches : true);
  useEffect(() => {
    if (!media) return;
    const change = () => setDesktop(media.matches);
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, [media]);
  return desktop;
}

/** « Continuer » submits the billing profile's form when it is on screen (`form`), else runs `onClick`. */
function PrimaryButton({ action, className = '' }: { action: Action; className?: string }) {
  return (
    <Button type={action.form ? 'submit' : 'button'} form={action.form} onClick={action.onClick} disabled={action.disabled} className={className} data-testid={action.testId}>
      {action.label}
    </Button>
  );
}

/** The users within the offer's range and never below members + pending invitations. */
function seatsWithin(offer: Offer, wanted: number, used: number): number {
  const v = offer.terms;
  if (v.max_seats === 1) return 1;
  const low = Math.max(v.min_seats, used, 1);
  const high = v.max_seats ?? Infinity;
  return Math.min(Math.max(wanted || low, low), high);
}

function Frame({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-pane">
      <header className="border-b border-line-soft bg-white">
        <div className="mx-auto flex max-w-[1040px] items-center gap-3 px-4 py-3">
          <Link to="/billing" className="flex items-center gap-2">
            <img src="/brand/sculpt-agent-icon.svg" alt="" className="h-6 w-6" draggable={false} />
            <span className="text-[14px] font-medium tracking-[-0.01em] text-ink">Sculpt</span>
          </Link>
          <span className="flex-1" />
          <span className="hidden text-[12px] text-faint sm:inline">🔒 {t('checkout.secureShort')}</span>
          <Link to="/billing" className="text-[13px] text-muted hover:text-ink">
            {t('checkout.leave')}
          </Link>
        </div>
      </header>
      <main className="mx-auto flex max-w-[1040px] flex-col gap-5 px-4 py-6">
        {children}
        <ManageCookies className="pt-6" />
      </main>
    </div>
  );
}

function Stepper({ step, skipBilling, onGo }: { step: Step; skipBilling: boolean; onGo: (s: Step) => void }) {
  const { t } = useTranslation();
  const labels: [Step, string][] = [
    [1, t('checkout.stepOffer')],
    [2, t('checkout.stepBilling')],
    [3, t('checkout.stepPayment')],
  ];
  return (
    <ol className="flex items-center gap-2 text-[13px]" aria-label={t('checkout.steps')}>
      {labels.map(([n, label], i) => {
        const state = n === step ? 'current' : n < step ? 'done' : 'todo';
        const skipped = n === 2 && skipBilling;
        return (
          <li key={n} className="flex items-center gap-2">
            {i > 0 && <span className="h-px w-6 bg-line sm:w-10" />}
            <button
              type="button"
              onClick={() => onGo(n)}
              disabled={state !== 'done' || skipped}
              aria-current={state === 'current' ? 'step' : undefined}
              className={`flex items-center gap-2 ${state === 'todo' || skipped ? 'text-faint' : 'text-ink'}`}
            >
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-medium ${
                  state === 'current' ? 'bg-accent text-white' : state === 'done' ? 'bg-accent-bg text-accent-dark' : 'border border-line text-faint'
                }`}
              >
                {state === 'done' ? '✓' : n}
              </span>
              <span className={state === 'current' ? 'font-medium' : ''}>{label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function OfferChoice({ offers, current, selected, onSelect }: { offers: Offer[]; current: string | null; selected: string; onSelect: (o: Offer) => void }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  return (
    <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label={t('checkout.stepOffer')}>
      {offers.map((o) => {
        const [price, credits] = offerPrice(o, t, l);
        const on = o.key === selected;
        return (
          <button
            key={o.key}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onSelect(o)}
            data-testid={`offer-${o.key}`}
            className={`flex flex-col gap-2 rounded-lg border p-4 text-left transition-colors ${on ? 'border-accent bg-accent-bg ring-1 ring-accent' : 'border-line-card bg-white hover:border-line'}`}
          >
            <span className="flex items-center gap-2">
              <span className="text-[15px] font-medium text-ink">{o.name}</span>
              {current === o.key && <Badge tone="accent">{t('checkout.yourOffer')}</Badge>}
            </span>
            <span className="flex flex-col text-[13px] text-ink" data-testid={`price-${o.key}`}>
              <span>{price}</span>
              {credits && <span className="text-[12px] text-muted">{credits}</span>}
            </span>
            <span className="text-[12px] text-muted">{o.terms.max_seats === 1 ? t('pricing.oneUser') : t('pricing.anyUsers')}</span>
            {offerFeatures(o, t).length > 0 && (
              <ul className="flex flex-col gap-0.5 text-[12px] text-muted">
                {offerFeatures(o, t).map((f) => (
                  <li key={f}>✓ {f}</li>
                ))}
              </ul>
            )}
          </button>
        );
      })}
    </div>
  );
}

function TierChoice({ offer, value, onChange }: { offer: Offer; value: string; onChange: (price: string) => void }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const shared = sharedCredits(offer);
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 font-mono text-[11px] uppercase tracking-[0.06em] text-faint">{t(shared ? 'checkout.tierLabelShared' : 'pricing.tierLabel')}</legend>
      {shared && Number(offer.terms.price_per_seat) > 0 && (
        <p className="-mt-1 text-[12px] text-muted" data-testid="seats-on-top">
          {t('checkout.seatsOnTop', { price: money(offer.terms.price_per_seat, offer.terms.currency, l) })}
        </p>
      )}
      <div className="grid gap-2 sm:grid-cols-3" role="radiogroup">
        {tiersOf(offer).map((tier) => {
          const on = Number(tier.price) === Number(value);
          const bonus = Math.round((Number(tier.usage) / Number(tier.price) - 1) * 100);
          return (
            <button
              key={tier.price}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChange(tier.price)}
              data-testid={`tier-${Number(tier.price)}`}
              className={`flex flex-col rounded-md border px-3 py-2 text-left text-[13px] ${on ? 'border-accent bg-accent-bg' : 'border-line-card bg-white hover:border-line'}`}
            >
              <span className="font-medium text-ink">{t('checkout.perMonth', { price: money(tier.price, offer.terms.currency, l) })}</span>
              <span className="text-[12px] text-muted">{t('pricing.tierUsage', { usage: money(tier.usage, offer.terms.currency, l) })}</span>
              {bonus > 0 && <span className="mt-0.5 text-[11px] font-medium text-accent-dark">{t('checkout.bonus', { bonus })}</span>}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function SeatsChoice({ offer, seats, used, onChange }: { offer: Offer; seats: number; used: number; onChange: (n: number) => void }) {
  const { t, i18n } = useTranslation();
  const low = Math.max(offer.terms.min_seats, used, 1);
  const high = offer.terms.max_seats ?? Infinity;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="font-mono text-[11px] uppercase tracking-[0.06em] text-faint">{t('billing.seats')}</span>
      <div className="flex items-center overflow-hidden rounded-md border border-line-btn bg-white">
        <button type="button" className="px-3 py-1.5 text-[15px] text-ink disabled:text-fainter" onClick={() => onChange(seats - 1)} disabled={seats <= low} aria-label={t('checkout.fewerUsers')}>
          −
        </button>
        <span className="min-w-10 text-center font-mono text-[14px] text-ink" data-testid="seats">
          {seats}
        </span>
        <button type="button" className="px-3 py-1.5 text-[15px] text-ink disabled:text-fainter" onClick={() => onChange(seats + 1)} disabled={seats >= high} aria-label={t('checkout.moreUsers')}>
          +
        </button>
      </div>
      <span className="text-[12px] text-muted">
        {t('checkout.perUser', { price: money(offer.terms.price_per_seat, offer.terms.currency, i18n.language) })}
        {used > 1 && ` · ${t('checkout.atLeast', { count: used })}`}
      </span>
    </div>
  );
}

function PackChoice({ packs, selected, onSelect }: { packs: CreditPack[]; selected: string; onSelect: (key: string) => void }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[13px] text-muted">{t('billing.creditsHint')}</p>
      <div className="grid gap-2 sm:grid-cols-2" role="radiogroup">
        {packs.map((p) => {
          const on = p.key === selected;
          return (
            <button
              key={p.key}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onSelect(p.key)}
              data-testid={`pack-${p.key}`}
              className={`flex flex-col rounded-md border px-3 py-2 text-left text-[13px] ${on ? 'border-accent bg-accent-bg' : 'border-line-card bg-white hover:border-line'}`}
            >
              <span className="font-medium text-ink">{money(p.price, p.currency, l)}</span>
              <span className="text-[12px] text-muted">
                {t('pricing.tierUsage', { usage: money(p.credit_eur, p.currency, l) })} · {p.validity_months ? t('pricing.validFor', { count: p.validity_months }) : t('pricing.noExpiry')}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Who is billed, on two lines, and « Modifier » (back to the billing step). */
function ProfileSummary({ org, onEdit }: { org: Organization; onEdit: () => void }) {
  const { t } = useTranslation();
  const ids = [org.siren && `SIREN ${org.siren}`, org.registration_number, org.vat_number && `${t('checkout.vatNumber')} ${org.vat_number}`].filter(Boolean).join(' · ');
  return (
    <div className="flex items-start justify-between gap-3 rounded-md bg-pane px-3 py-2.5 text-[12.5px]" data-testid="profile-summary">
      <div className="flex min-w-0 flex-col">
        <span className="text-faint">{t('checkout.billedTo')}</span>
        <span className="truncate text-ink">
          {org.billing_name} · {org.billing_street}, {org.billing_zip} {org.billing_city}, {org.billing_country}
        </span>
        {ids && <span className="truncate text-muted">{ids}</span>}
      </div>
      <button type="button" className="shrink-0 text-accent underline" onClick={onEdit}>
        {t('checkout.edit')}
      </button>
    </div>
  );
}

/** Excl. VAT, the VAT line as the scheme says, incl. VAT, in `currency`. */
function Amounts({ vat, amount, currency }: { vat: VatFields | null; amount: string; currency: string }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const row = (label: string, value: string, strong = false) => (
    <div className={`flex justify-between gap-3 ${strong ? 'border-t border-line-row pt-2 text-[15px] font-medium text-ink' : 'text-muted'}`}>
      <span>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
  const scheme = vat?.vat_scheme ?? null;
  return (
    <div className="flex flex-col gap-1.5 text-[13px]" data-testid="amounts">
      {row(t('checkout.exclVat'), money(amount, currency, l))}
      {!vat || scheme === null ? (
        <span className="text-[12.5px] text-faint">{t('billing.vat.noProfile')}</span>
      ) : scheme === 'review' ? (
        <span className="text-[12.5px] text-warn">{t('billing.vat.held')}</span>
      ) : (
        <>
          {row(
            scheme === 'reverse_charge' ? t('billing.vat.reverseCharge') : scheme === 'outside_scope' ? t('billing.vat.outsideScope') : t('checkout.vatLine', { rate: vatRate(vat.vat_rate ?? '0', l) }),
            money(vat.vat_amount ?? '0', currency, l),
          )}
          {vat.total !== null && row(t('checkout.inclVat'), money(vat.total, currency, l), true)}
        </>
      )}
    </div>
  );
}

/** Incl. VAT when the VAT is known (not held for review): `amount` × (1 + rate), as the API rounds. */
function withVat(amount: string | number, vat: VatFields | null): number | null {
  if (!vat || vat.vat_scheme === null || vat.vat_scheme === 'review' || vat.vat_rate === null) return null;
  const ht = Number(amount);
  return ht + Math.round(ht * Number(vat.vat_rate) * 100) / 100;
}

function Summary({ mode, unchanged, offer, tier, seats, pack, order, quote, packQuote, estimate, sub, loading, profileComplete, action }: {
  mode: Mode; unchanged: boolean; offer: Offer | null; tier: string; seats: number; pack: CreditPack | null; order: Order | null; quote: Quote | null; packQuote: PackQuote | null;
  estimate: number; sub: BillingState['subscription']; loading: boolean; profileComplete: boolean; action: Action | null;
}) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const tierRow = offer ? tiersOf(offer).find((x) => Number(x.price) === Number(tier)) : undefined;
  // Today's amount, incl. VAT once known; what follows each month, incl. VAT on the same terms.
  let today: string;
  const notes: string[] = [];
  let then: string | null = null;
  const vatLater = profileComplete ? null : t('checkout.vatLater');
  if (mode === 'order' && order) {
    today = money(order.total ?? order.amount, order.currency, l);
    notes.push(order.total === null ? t('checkout.exclVatShort') : t('checkout.inclVatShort'));
  } else if (mode === 'pack') {
    const ttc = packQuote?.total ?? null;
    today = ttc !== null ? money(ttc, packQuote!.currency, l) : pack ? money(pack.price, pack.currency, l) : '—';
    notes.push(ttc !== null ? t('checkout.inclVatShort') : t('checkout.exclVatShort'));
    if (ttc === null && vatLater) notes.push(vatLater);
  } else if (quote) {
    const scheduled = quote.applies === 'period_end';
    const monthly = withVat(quote.monthly_total, quote);
    today = money(scheduled ? 0 : quote.total ?? quote.amount, quote.currency, l);
    if (scheduled) notes.push(t('checkout.appliesOn', { date: date(quote.period_end, l) }));
    else {
      notes.push(quote.total !== null ? t('checkout.inclVatShort') : t('checkout.exclVatShort'));
      if (quote.order_kind === 'change' || quote.order_kind === 'seats') notes.push(t('checkout.prorata', { date: date(quote.period_end, l) }));
    }
    then = monthly !== null ? t('checkout.thenInclVat', { price: money(monthly, quote.currency, l) }) : t('checkout.then', { price: money(quote.monthly_total, quote.currency, l) });
    if (monthly === null && vatLater) notes.push(vatLater);
  } else {
    const cur = offer?.terms.currency ?? sub?.currency ?? 'EUR';
    today = money(estimate, cur, l);
    notes.push(t('checkout.exclVatShort'));
    then = t('checkout.then', { price: money(estimate, cur, l) });
    if (vatLater) notes.push(vatLater);
  }
  const note = notes.join(', ');
  const lines: string[] = [];
  if (mode === 'order' && order) order.lines.forEach((x) => lines.push(x.description));
  else if (mode === 'pack' && pack) lines.push(pack.name, t('pricing.tierUsage', { usage: money(pack.credit_eur, pack.currency, l) }));
  else if (offer) lines.push(offer.name);
  // The offer's price in its parts (seats, credits, total per month), before the amounts paid.
  const breakdown = mode === 'offer' && offer ? <PriceBreakdown offer={offer} tierPrice={tierRow?.price ?? null} seats={seats} vat={quote} /> : null;
  return (
    <>
      <aside className="hidden flex-col gap-4 rounded-lg border border-line-card bg-white p-5 lg:sticky lg:top-6 lg:flex" data-testid="summary">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.06em] text-faint">{t('checkout.summary')}</h2>
        <ul className="flex flex-col gap-1 text-[13px]">
          {lines.map((x, i) => (
            <li key={`${x}-${i}`} className={i === 0 ? 'text-[15px] font-medium text-ink' : 'text-muted'}>
              {x}
            </li>
          ))}
        </ul>
        {breakdown}
        {sub && mode === 'offer' && !unchanged && <p className="text-[12px] text-faint">{t('checkout.currently', { offer: sub.offer_name, price: money(sub.monthly_total, sub.currency, l) })}</p>}
        {unchanged ? (
          <p className="border-t border-line-row pt-3 text-[13px] font-medium text-ink" data-testid="current-offer">{t('checkout.noChange')}</p>
        ) : (
        <div className="flex flex-col gap-0.5 border-t border-line-row pt-3">
          <span className="text-[12px] text-muted">{t('checkout.today')}</span>
          <span className="flex items-baseline gap-2">
            <strong className="font-mono text-[24px] font-medium text-ink" data-testid="today">
              {today}
            </strong>
            {loading && <span className="inline-block h-3 w-3 animate-pc-spin rounded-full border-2 border-line border-t-accent" aria-hidden />}
          </span>
          {note && <span className="text-[12px] text-muted">{note}</span>}
          {then && <span className="mt-1 text-[12.5px] text-ink" data-testid="then">{then}</span>}
        </div>
        )}
      </aside>
      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-line-card bg-white px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-4px_12px_rgba(0,0,0,0.04)] lg:hidden" data-testid="summary-bar">
        <div className="mx-auto flex max-w-[1040px] flex-col gap-2.5">
          <div className="flex items-baseline justify-between gap-3 text-[13px]">
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-muted">{lines[0] ?? ''}</span>
              {then && !unchanged && <span className="truncate text-[11.5px] text-muted">{then}</span>}
            </span>
            {unchanged ? (
              <span className="shrink-0 text-[13px] font-medium text-ink">{t('checkout.yourOffer')}</span>
            ) : (
              <span className="flex shrink-0 flex-col items-end">
                <strong className="font-mono text-[17px] font-medium text-ink">{today}</strong>
                <span className="text-[11px] text-faint">{t('checkout.today')}</span>
              </span>
            )}
          </div>
          {action && <PrimaryButton action={action} className="w-full py-2.5! text-[14px]" />}
        </div>
      </div>
    </>
  );
}

function DoneScreen({ done, billing }: { done: Done; billing: BillingState }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => ref.current?.focus(), []);
  const text = useMemo(() => {
    if (done.kind === 'applied') return t('checkout.doneApplied');
    if (done.kind === 'scheduled') return t('checkout.doneScheduled', { date: date(done.date, l) });
    return t('checkout.doneOrdered', { number: done.order.id, total: money(done.order.total ?? done.order.amount, done.order.currency, l) });
  }, [done, l, t]);
  return (
    <div ref={ref} tabIndex={-1} className="mx-auto flex w-full max-w-[560px] flex-col gap-4 rounded-lg border border-line-card bg-white p-6 text-[14px] outline-hidden" data-testid="done">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-bg text-[18px] text-accent-dark">✓</span>
      <p className="text-ink">{text}</p>
      {done.kind === 'ordered' && done.order.vat_scheme === 'review' && <Alert kind="warn">{t('billing.vat.held')}</Alert>}
      {done.kind === 'ordered' && done.order.vat_scheme !== 'review' && billing.payment_instructions && <p className="whitespace-pre-line font-mono text-[12px] text-muted">{billing.payment_instructions}</p>}
      <div>
        <Link to="/billing" className="text-accent underline">
          {t('checkout.backToBilling')}
        </Link>
      </div>
    </div>
  );
}
